export const ROOM_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'tr', name: 'Turkish' },
  { code: 'ja', name: 'Japanese' },
] as const

export type LanguageCode = typeof ROOM_LANGUAGES[number]['code']

export interface ChatPlaceholderTemplates {
  interview: (name: string) => string
  interviewFallback: string
  group: (name: string) => string
  groupFallback: string
  emptyState: string
  /** Headline over the roster while a 1:1 conversation has no messages. */
  emptyHeadlineInterview: (name: string) => string
  /** Headline over the roster while a group conversation has no messages. */
  emptyHeadlineGroup: string
}

export const CHAT_PLACEHOLDERS: Record<string, ChatPlaceholderTemplates> = {
  en: {
    interview: (name) => `Message ${name}...`,
    interviewFallback: 'What would you like to know?',
    group: (name) => `Ask ${name} and others...`,
    groupFallback: 'What would you like to ask the group?',
    emptyState: 'Start the conversation by typing a message below.',
    emptyHeadlineInterview: (name) => `Ask ${name} anything`,
    emptyHeadlineGroup: 'Ask the group anything',
  },
  es: {
    interview: (name) => `Mensaje para ${name}...`,
    interviewFallback: '¿Qué te gustaría saber?',
    group: (name) => `Pregunta a ${name} y otros...`,
    groupFallback: '¿Qué te gustaría preguntarle al grupo?',
    emptyState: 'Inicia la conversación escribiendo un mensaje abajo.',
    emptyHeadlineInterview: (name) => `Pregúntale lo que quieras a ${name}`,
    emptyHeadlineGroup: 'Pregúntale lo que quieras al grupo',
  },
  fr: {
    interview: (name) => `Message à ${name}...`,
    interviewFallback: 'Que souhaitez-vous savoir?',
    group: (name) => `Demandez à ${name} et d'autres...`,
    groupFallback: 'Que souhaitez-vous demander au groupe?',
    emptyState: 'Commencez la conversation en tapant un message ci-dessous.',
    emptyHeadlineInterview: (name) => `Posez n'importe quelle question à ${name}`,
    emptyHeadlineGroup: "Posez n'importe quelle question au groupe",
  },
  de: {
    interview: (name) => `Nachricht an ${name}...`,
    interviewFallback: 'Was möchten Sie wissen?',
    group: (name) => `Fragen Sie ${name} und andere...`,
    groupFallback: 'Was möchten Sie die Gruppe fragen?',
    emptyState: 'Starten Sie das Gespräch, indem Sie unten eine Nachricht eingeben.',
    emptyHeadlineInterview: (name) => `Fragen Sie ${name} alles`,
    emptyHeadlineGroup: 'Fragen Sie die Gruppe alles',
  },
  pt: {
    interview: (name) => `Mensagem para ${name}...`,
    interviewFallback: 'O que você gostaria de saber?',
    group: (name) => `Pergunte a ${name} e outros...`,
    groupFallback: 'O que você gostaria de perguntar ao grupo?',
    emptyState: 'Inicie a conversa digitando uma mensagem abaixo.',
    emptyHeadlineInterview: (name) => `Pergunte o que quiser a ${name}`,
    emptyHeadlineGroup: 'Pergunte o que quiser ao grupo',
  },
  it: {
    interview: (name) => `Messaggio a ${name}...`,
    interviewFallback: 'Cosa vorresti sapere?',
    group: (name) => `Chiedi a ${name} e ad altri...`,
    groupFallback: 'Cosa vorresti chiedere al gruppo?',
    emptyState: 'Avvia la conversazione digitando un messaggio qui sotto.',
    emptyHeadlineInterview: (name) => `Chiedi qualsiasi cosa a ${name}`,
    emptyHeadlineGroup: 'Chiedi qualsiasi cosa al gruppo',
  },
  nl: {
    interview: (name) => `Bericht aan ${name}...`,
    interviewFallback: 'Wat wilt u weten?',
    group: (name) => `Vraag het aan ${name} en anderen...`,
    groupFallback: 'Wat wilt u de groep vragen?',
    emptyState: 'Begin het gesprek door hieronder een bericht te typen.',
    emptyHeadlineInterview: (name) => `Vraag ${name} wat u maar wilt`,
    emptyHeadlineGroup: 'Vraag de groep wat u maar wilt',
  },
  tr: {
    interview: (name) => `${name} kişisine mesaj...`,
    interviewFallback: 'Ne öğrenmek istersiniz?',
    group: (name) => `${name} ve diğerlerine sorun...`,
    groupFallback: 'Gruba ne sormak istersiniz?',
    emptyState: 'Aşağıya bir mesaj yazarak sohbeti başlatın.',
    emptyHeadlineInterview: (name) => `${name} kişisine istediğinizi sorun`,
    emptyHeadlineGroup: 'Gruba istediğinizi sorun',
  },
  ja: {
    interview: (name) => `${name}さんへのメッセージ...`,
    interviewFallback: '何をお聞きになりたいですか？',
    group: (name) => `${name}さんたちに質問する...`,
    groupFallback: 'グループに何をお聞きになりたいですか？',
    emptyState: '下にメッセージを入力して会話を始めましょう。',
    emptyHeadlineInterview: (name) => `${name}さんに何でも聞いてみましょう`,
    emptyHeadlineGroup: 'グループに何でも聞いてみましょう',
  },
}

export function getChatPlaceholders(language: string): ChatPlaceholderTemplates {
  return CHAT_PLACEHOLDERS[language] ?? CHAT_PLACEHOLDERS['en']
}

export interface HomePlaceholderTemplates {
  chat: string
  study: string
  data: string
}

/**
 * Home Lab composer placeholders, one triplet per mode.
 *
 * Chat and Study still name their subject, because both are pointed at a twin
 * audience and the sentence has a "them" to refer to.
 *
 * Data (the Ask mode) no longer does. Its line used to read "…from your
 * documents", from when Ask was library retrieval and nothing else. Ask is now
 * the full assistant: it reads the library, searches the web, runs commands and
 * arms workflows, so naming documents advertised the narrowest of the things it
 * can do and quietly hid the rest. The replacement names no source at all and
 * simply opens the turn. Do not "restore" the documents wording; it would be a
 * smaller promise than the mode keeps.
 */
export const HOME_PLACEHOLDERS: Record<string, HomePlaceholderTemplates> = {
  en: {
    chat: 'What do you want to ask them?',
    study: 'What do you want to learn from them?',
    data: 'How can I help?',
  },
  es: {
    chat: '¿Qué quieres preguntarles?',
    study: '¿Qué quieres aprender de ellos?',
    data: '¿En qué puedo ayudarte?',
  },
  fr: {
    chat: 'Que voulez-vous leur demander?',
    study: "Que voulez-vous apprendre d'eux?",
    data: 'Comment puis-je vous aider?',
  },
  de: {
    chat: 'Was möchten Sie sie fragen?',
    study: 'Was möchten Sie von ihnen erfahren?',
    data: 'Wie kann ich Ihnen helfen?',
  },
  pt: {
    chat: 'O que você quer perguntar a eles?',
    study: 'O que você quer aprender com eles?',
    data: 'Como posso ajudar?',
  },
  it: {
    chat: 'Cosa vuoi chiedere loro?',
    study: 'Cosa vuoi imparare da loro?',
    data: 'Come posso aiutarti?',
  },
  nl: {
    chat: 'Wat wilt u hen vragen?',
    study: 'Wat wilt u van hen leren?',
    data: 'Waarmee kan ik u helpen?',
  },
  tr: {
    chat: 'Onlara ne sormak istersiniz?',
    study: 'Onlardan ne öğrenmek istersiniz?',
    data: 'Size nasıl yardımcı olabilirim?',
  },
  ja: {
    chat: '彼らに何を聞きたいですか？',
    study: '彼らから何を学びたいですか？',
    data: '何をお手伝いしましょうか？',
  },
}

export function getHomePlaceholders(language: string): HomePlaceholderTemplates {
  return HOME_PLACEHOLDERS[language] ?? HOME_PLACEHOLDERS['en']
}

export interface ThreadPlaceholderTemplates {
  /** The follow-up composer under a thread. */
  askNext: string
  /**
   * The same composer while a REVISABLE gate is docked on it, where a send is
   * routed into the gate as a revision rather than asked as a new question.
   * Gated on the published revision handle, not on the dock: a viewer who does
   * not own the run sees the card but their send still reaches the assistant,
   * so they keep `askNext` and are not promised an edit they cannot make.
   */
  askChanges: string
  /**
   * The wait before any tool has reported in, and the fallback whenever the
   * running tools are ones this build has no name for. It claims nothing beyond
   * "the turn is alive", which is the only thing true in every case.
   */
  working: string
  /**
   * The retrieval phase. `{targets}` is replaced with the RUNNING tools' targets
   * below, joined in the thread's own language. Spoken on the waiting row
   * (`aria-label`); the visible row uses `searchingLead` plus chips instead.
   */
  searching: string
  /**
   * Same phase as `searching`, when at least one running named tool carried a
   * `query`. `{query}` is the model's own string (clipped), not translated.
   */
  searchingFor: string
  /**
   * Visible verb ahead of the source chips. No `{targets}` and no query: the
   * chips carry which sources are running, and a long lookup sits on the chip
   * tooltip rather than in this word.
   */
  searchingLead: string
  /** Every tool has reported back and the answer has not started arriving yet. */
  composing: string
  /** Workflow start race: create has been posted, run row not yet on the client. */
  runStarting: string
  /** Active `prompt` step is running (e.g. drafting questions). */
  runPreparing: string
  /** Active `quick_study` step is running or waiting on the external study. */
  runSampling: string
  /** Active `report` step is running. */
  runWriting: string
  /** Active `slack_webhook` step is running. */
  runPosting: string
  /**
   * What each tool is working through, as the fragment `searching` interpolates.
   * Keyed by the wire's tool name (`AssistantToolName`), and deliberately open:
   * a tool added later is one line per language here and no code change at all.
   *
   * A tool absent from this map is not named out loud. The stream does carry a
   * server `label`, but it is English-only, so borrowing it would put an English
   * fragment inside a Portuguese sentence. An unnamed tool falls back to
   * `working` instead, which stays true whatever is running.
   */
  targets: Record<string, string>
  /**
   * Noun on the citation-style chip. Separate from `targets` because those
   * fragments carry sentence prepositions (`the web`, `en la web`) that read
   * wrong inside a pill.
   */
  chips: Record<string, string>
}

/**
 * Knowledge thread prose, in the language the THREAD answers in
 * (docs/plans/home-lab-composer-language-and-recent-study.md, D3 and D4).
 *
 * The thread already carries its own language, picked once when it was created and
 * persisted so every turn stays in it, so this table needs no new state: it is the same
 * value the answer itself is written in. A Portuguese answer under an English "Ask next"
 * was the mismatch worth closing.
 *
 * D3 still fences this: these are PROSE, the words the reader reads. The scope chip, the
 * item picker and the sources count stay English, because they are product controls and
 * translating them would need an i18n layer that does not exist.
 *
 * The waiting lines are built as a template plus per-tool fragments rather than one
 * finished sentence per situation, so the set of tools a turn can run is free to grow
 * without this table growing a sentence for every combination of them. Each target
 * carries its own preposition, because which one is right is a fact about the language
 * and not about the tool.
 */
export const THREAD_PLACEHOLDERS: Record<string, ThreadPlaceholderTemplates> = {
  en: {
    askNext: 'Ask next\u2026',
    askChanges: 'Ask for changes\u2026',
    working: 'Looking into this\u2026',
    searching: 'Searching {targets}\u2026',
    searchingFor: 'Searching {targets} for "{query}"\u2026',
    searchingLead: 'Searching',
    composing: 'Writing the answer\u2026',
    runStarting: 'Starting\u2026',
    runPreparing: 'Preparing your questions\u2026',
    runSampling: 'Asking your twins\u2026',
    runWriting: 'Writing\u2026',
    runPosting: 'Posting\u2026',
    targets: {
      document_search: 'your library',
      web_search: 'the web',
      read_skill: 'your skills',
      read_workflow: 'your workflows',
      read_audience: 'your audiences',
      analyze_audience: 'your audience analysis',
      read_results: 'your results',
      read_artifact: 'the artifact',
      update_artifact: 'the artifact',
      create_artifact: 'the creatives artifact',
      delete_artifact: 'the artifact',
      run_workflow: 'a workflow',
    },
    chips: {
      document_search: 'Library',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analysis',
      read_results: 'Results',
      read_artifact: 'Artifact',
      update_artifact: 'Artifact',
      create_artifact: 'Creatives',
      delete_artifact: 'Artifact',
      run_workflow: 'Workflow',
    },
  },
  es: {
    askNext: 'Pregunta lo siguiente\u2026',
    askChanges: 'Pide cambios\u2026',
    working: 'Trabajando en ello\u2026',
    searching: 'Buscando {targets}\u2026',
    searchingFor: 'Buscando {targets} por "{query}"\u2026',
    searchingLead: 'Buscando',
    composing: 'Escribiendo la respuesta\u2026',
    runStarting: 'Iniciando\u2026',
    runPreparing: 'Preparando tus preguntas\u2026',
    runSampling: 'Preguntando a tus twins\u2026',
    runWriting: 'Escribiendo\u2026',
    runPosting: 'Publicando\u2026',
    targets: {
      document_search: 'en tus documentos',
      web_search: 'en la web',
      read_skill: 'en tus skills',
      read_workflow: 'en tus flujos',
      read_audience: 'en tus audiencias',
      analyze_audience: 'en el análisis de audiencia',
      read_results: 'en tus resultados',
      read_artifact: 'el artefacto',
      update_artifact: 'el artefacto',
      create_artifact: 'el artefacto de creatividades',
      delete_artifact: 'el artefacto',
      run_workflow: 'un flujo',
    },
    chips: {
      document_search: 'Biblioteca',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Flujos',
      read_audience: 'Audiencia',
      analyze_audience: 'Análisis',
      read_results: 'Resultados',
      read_artifact: 'Artefacto',
      update_artifact: 'Artefacto',
      create_artifact: 'Creatividades',
      delete_artifact: 'Artefacto',
      run_workflow: 'Flujo',
    },
  },
  fr: {
    askNext: 'Posez la question suivante\u2026',
    askChanges: 'Demandez des modifications\u2026',
    working: 'Traitement en cours\u2026',
    searching: 'Recherche {targets}\u2026',
    searchingFor: 'Recherche {targets} pour "{query}"\u2026',
    searchingLead: 'Recherche',
    composing: 'R\u00e9daction de la r\u00e9ponse\u2026',
    runStarting: 'D\u00e9marrage\u2026',
    runPreparing: 'Pr\u00e9paration de vos questions\u2026',
    runSampling: 'Interrogation de vos twins\u2026',
    runWriting: 'R\u00e9daction\u2026',
    runPosting: 'Publication\u2026',
    targets: {
      document_search: 'dans vos documents',
      web_search: 'sur le web',
      read_skill: 'dans vos skills',
      read_workflow: 'dans vos flux',
      read_audience: 'dans vos audiences',
      analyze_audience: 'dans l\u2019analyse d\u2019audience',
      read_results: 'dans vos r\u00e9sultats',
      read_artifact: 'l\u2019artefact',
      update_artifact: 'l\u2019artefact',
      create_artifact: 'l\u2019artefact cr\u00e9atif',
      delete_artifact: 'l\u2019artefact',
      run_workflow: 'un flux',
    },
    chips: {
      document_search: 'Biblioth\u00e8que',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Flux',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'R\u00e9sultats',
      read_artifact: 'Artefact',
      update_artifact: 'Artefact',
      create_artifact: 'Cr\u00e9atifs',
      delete_artifact: 'Artefact',
      run_workflow: 'Flux',
    },
  },
  de: {
    askNext: 'Als N\u00e4chstes fragen\u2026',
    askChanges: '\u00c4nderungen anfragen\u2026',
    working: 'Wird bearbeitet\u2026',
    searching: 'Suche {targets}\u2026',
    searchingFor: 'Suche {targets} nach "{query}"\u2026',
    searchingLead: 'Suche',
    composing: 'Antwort wird geschrieben\u2026',
    runStarting: 'Wird gestartet\u2026',
    runPreparing: 'Fragen werden vorbereitet\u2026',
    runSampling: 'Ihre Twins werden befragt\u2026',
    runWriting: 'Wird geschrieben\u2026',
    runPosting: 'Wird gepostet\u2026',
    targets: {
      document_search: 'in Ihren Dokumenten',
      web_search: 'im Web',
      read_skill: 'in Ihren Skills',
      read_workflow: 'in Ihren Workflows',
      read_audience: 'in Ihren Audiences',
      analyze_audience: 'in Ihrer Audience-Analyse',
      read_results: 'in Ihren Ergebnissen',
      read_artifact: 'im Artefakt',
      update_artifact: 'im Artefakt',
      create_artifact: 'im Kreativ-Artefakt',
      delete_artifact: 'im Artefakt',
      run_workflow: 'einen Workflow',
    },
    chips: {
      document_search: 'Bibliothek',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'Ergebnisse',
      read_artifact: 'Artefakt',
      update_artifact: 'Artefakt',
      create_artifact: 'Creatives',
      delete_artifact: 'Artefakt',
      run_workflow: 'Workflow',
    },
  },
  pt: {
    askNext: 'Pergunte a seguir\u2026',
    askChanges: 'Pe\u00e7a altera\u00e7\u00f5es\u2026',
    working: 'Trabalhando nisso\u2026',
    searching: 'Pesquisando {targets}\u2026',
    searchingFor: 'Pesquisando {targets} por "{query}"\u2026',
    searchingLead: 'Pesquisando',
    composing: 'Escrevendo a resposta\u2026',
    runStarting: 'Iniciando\u2026',
    runPreparing: 'Preparando suas perguntas\u2026',
    runSampling: 'Perguntando aos seus twins\u2026',
    runWriting: 'Escrevendo\u2026',
    runPosting: 'Publicando\u2026',
    targets: {
      document_search: 'nos seus documentos',
      web_search: 'na web',
      read_skill: 'nas suas skills',
      read_workflow: 'nos seus fluxos',
      read_audience: 'nas suas audi\u00eancias',
      analyze_audience: 'na an\u00e1lise da audi\u00eancia',
      read_results: 'nos seus resultados',
      read_artifact: 'no artefato',
      update_artifact: 'no artefato',
      create_artifact: 'no artefato de criativos',
      delete_artifact: 'no artefato',
      run_workflow: 'um fluxo',
    },
    chips: {
      document_search: 'Biblioteca',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Fluxos',
      read_audience: 'Audi\u00eancia',
      analyze_audience: 'An\u00e1lise',
      read_results: 'Resultados',
      read_artifact: 'Artefato',
      update_artifact: 'Artefato',
      create_artifact: 'Criativos',
      delete_artifact: 'Artefato',
      run_workflow: 'Fluxo',
    },
  },
  it: {
    askNext: 'Chiedi altro\u2026',
    askChanges: 'Chiedi modifiche\u2026',
    working: 'Ci sto lavorando\u2026',
    searching: 'Ricerca {targets}\u2026',
    searchingFor: 'Ricerca {targets} per "{query}"\u2026',
    searchingLead: 'Ricerca',
    composing: 'Sto scrivendo la risposta\u2026',
    runStarting: 'Avvio\u2026',
    runPreparing: 'Preparazione delle domande\u2026',
    runSampling: 'Sto chiedendo ai tuoi twin\u2026',
    runWriting: 'Sto scrivendo\u2026',
    runPosting: 'Sto pubblicando\u2026',
    targets: {
      document_search: 'nei tuoi documenti',
      web_search: 'nel web',
      read_skill: 'nelle tue skill',
      read_workflow: 'nei tuoi flussi',
      read_audience: 'nelle tue audience',
      analyze_audience: 'nell\u2019analisi audience',
      read_results: 'nei tuoi risultati',
      read_artifact: 'nell\u2019artefatto',
      update_artifact: 'nell\u2019artefatto',
      create_artifact: 'nell\u2019artefatto creativo',
      delete_artifact: 'nell\u2019artefatto',
      run_workflow: 'un flusso',
    },
    chips: {
      document_search: 'Libreria',
      web_search: 'Web',
      read_skill: 'Skill',
      read_workflow: 'Flussi',
      read_audience: 'Audience',
      analyze_audience: 'Analisi',
      read_results: 'Risultati',
      read_artifact: 'Artefatto',
      update_artifact: 'Artefatto',
      create_artifact: 'Creativi',
      delete_artifact: 'Artefatto',
      run_workflow: 'Flusso',
    },
  },
  nl: {
    askNext: 'Stel de volgende vraag\u2026',
    askChanges: 'Vraag om wijzigingen\u2026',
    working: 'Bezig\u2026',
    searching: 'Zoeken {targets}\u2026',
    searchingFor: 'Zoeken {targets} naar "{query}"\u2026',
    searchingLead: 'Zoeken',
    composing: 'Het antwoord wordt geschreven\u2026',
    runStarting: 'Bezig met starten\u2026',
    runPreparing: 'Uw vragen worden voorbereid\u2026',
    runSampling: 'Uw twins worden ondervraagd\u2026',
    runWriting: 'Bezig met schrijven\u2026',
    runPosting: 'Bezig met plaatsen\u2026',
    targets: {
      document_search: 'in uw documenten',
      web_search: 'op het web',
      read_skill: 'in uw skills',
      read_workflow: 'in uw workflows',
      read_audience: 'in uw audiences',
      analyze_audience: 'in uw audience-analyse',
      read_results: 'in uw resultaten',
      read_artifact: 'in het artefact',
      update_artifact: 'in het artefact',
      create_artifact: 'in het creatives-artefact',
      delete_artifact: 'in het artefact',
      run_workflow: 'een workflow',
    },
    chips: {
      document_search: 'Bibliotheek',
      web_search: 'Web',
      read_skill: 'Skills',
      read_workflow: 'Workflows',
      read_audience: 'Audience',
      analyze_audience: 'Analyse',
      read_results: 'Resultaten',
      read_artifact: 'Artefact',
      update_artifact: 'Artefact',
      create_artifact: 'Creatives',
      delete_artifact: 'Artefact',
      run_workflow: 'Workflow',
    },
  },
  tr: {
    askNext: 'Sonra ne sormak istersiniz\u2026',
    askChanges: 'De\u011fi\u015fiklik isteyin\u2026',
    working: '\u00dczerinde \u00e7al\u0131\u015f\u0131l\u0131yor\u2026',
    searching: '{targets} aran\u0131yor\u2026',
    searchingFor: '{targets} i\u00e7inde "{query}" aran\u0131yor\u2026',
    searchingLead: 'Aran\u0131yor',
    composing: 'Yan\u0131t yaz\u0131l\u0131yor\u2026',
    runStarting: 'Ba\u015flat\u0131l\u0131yor\u2026',
    runPreparing: 'Sorular\u0131n\u0131z haz\u0131rlan\u0131yor\u2026',
    runSampling: 'Twinlerinize soruluyor\u2026',
    runWriting: 'Yaz\u0131l\u0131yor\u2026',
    runPosting: 'G\u00f6nderiliyor\u2026',
    targets: {
      document_search: 'Belgeleriniz',
      web_search: 'Web',
      read_skill: 'Becerileriniz',
      read_workflow: 'I\u015f ak\u0131\u015flar\u0131n\u0131z',
      read_audience: 'Kitleleriniz',
      analyze_audience: 'Kitle analiziniz',
      read_results: 'Sonu\u00e7lar\u0131n\u0131z',
      read_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      update_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      create_artifact: 'kreatif \u00e7\u0131kt\u0131n\u0131z',
      delete_artifact: '\u00c7\u0131kt\u0131n\u0131z',
      run_workflow: 'bir i\u015f ak\u0131\u015f\u0131',
    },
    chips: {
      document_search: 'Kitapl\u0131k',
      web_search: 'Web',
      read_skill: 'Beceriler',
      read_workflow: 'I\u015f ak\u0131\u015flar\u0131',
      read_audience: 'Kitle',
      analyze_audience: 'Analiz',
      read_results: 'Sonu\u00e7lar',
      read_artifact: '\u00c7\u0131kt\u0131',
      update_artifact: '\u00c7\u0131kt\u0131',
      create_artifact: 'Kreatifler',
      delete_artifact: '\u00c7\u0131kt\u0131',
      run_workflow: 'I\u015f ak\u0131\u015f\u0131',
    },
  },
  ja: {
    askNext: '\u7d9a\u3051\u3066\u8cea\u554f\u3059\u308b\u2026',
    askChanges: '\u5909\u66f4\u3092\u30ea\u30af\u30a8\u30b9\u30c8\u2026',
    working: '\u51e6\u7406\u3057\u3066\u3044\u307e\u3059\u2026',
    searching: '{targets}\u3092\u691c\u7d22\u3057\u3066\u3044\u307e\u3059\u2026',
    searchingFor:
      '{targets}\u3067\u300c{query}\u300d\u3092\u691c\u7d22\u3057\u3066\u3044\u307e\u3059\u2026',
    searchingLead: '\u691c\u7d22\u4e2d',
    composing: '\u56de\u7b54\u3092\u4f5c\u6210\u3057\u3066\u3044\u307e\u3059\u2026',
    runStarting: '\u958b\u59cb\u3057\u3066\u3044\u307e\u3059\u2026',
    runPreparing: '\u8cea\u554f\u3092\u6e96\u5099\u3057\u3066\u3044\u307e\u3059\u2026',
    runSampling: '\u30c4\u30a4\u30f3\u306b\u8cea\u554f\u3057\u3066\u3044\u307e\u3059\u2026',
    runWriting: '\u4f5c\u6210\u3057\u3066\u3044\u307e\u3059\u2026',
    runPosting: '\u6295\u7a3f\u3057\u3066\u3044\u307e\u3059\u2026',
    targets: {
      document_search: '\u30c9\u30ad\u30e5\u30e1\u30f3\u30c8',
      web_search: '\u30a6\u30a7\u30d6',
      read_skill: '\u30b9\u30ad\u30eb',
      read_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      read_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9',
      analyze_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9\u5206\u6790',
      read_results: '\u7d50\u679c',
      read_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      update_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      create_artifact: '\u30af\u30ea\u30a8\u30a4\u30c6\u30a3\u30d6',
      delete_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      run_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
    },
    chips: {
      document_search: '\u30e9\u30a4\u30d6\u30e9\u30ea',
      web_search: 'Web',
      read_skill: '\u30b9\u30ad\u30eb',
      read_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
      read_audience: '\u30aa\u30fc\u30c7\u30a3\u30a8\u30f3\u30b9',
      analyze_audience: '\u5206\u6790',
      read_results: '\u7d50\u679c',
      read_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      update_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      create_artifact: '\u30af\u30ea\u30a8\u30a4\u30c6\u30a3\u30d6',
      delete_artifact: '\u30a2\u30fc\u30c6\u30a3\u30d5\u30a1\u30af\u30c8',
      run_workflow: '\u30ef\u30fc\u30af\u30d5\u30ed\u30fc',
    },
  },
}

/**
 * The table key a thread's language actually resolves to, English for anything
 * absent.
 *
 * Exported because the waiting line needs it twice over: once to pick the copy,
 * and once more to join the target fragments, which has to happen in the SAME
 * language the fragments came from. Resolving separately in the caller is how a
 * Korean thread would get English fragments joined by a Korean conjunction.
 */
export function resolveThreadLanguage(language?: string | null): string {
  return language && THREAD_PLACEHOLDERS[language] ? language : 'en'
}

export function getThreadPlaceholders(language?: string | null): ThreadPlaceholderTemplates {
  return THREAD_PLACEHOLDERS[resolveThreadLanguage(language)]
}
